/** The subset of `Storage` that `safeLocalStorage` needs from its backing store. */
export type SafeLocalStorageBackend = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const defaultBackend = (): SafeLocalStorageBackend | undefined => globalThis.localStorage;

/**
 * Resolved on every call rather than captured once: `ember-window-mock` builds a
 * fresh `Storage` for each test, so a captured reference would go stale after the
 * first one.
 */
let resolveBackend = defaultBackend;

/**
 * Points `safeLocalStorage` at a different backing store.
 *
 * The qonto-js Ember test setup uses this to route kit storage through
 * `ember-window-mock`, which resets between tests. Without it, kit code reads the
 * real `localStorage` — shared by every test in a browser instance — and dismissal
 * flags written by one test silently change behaviour in later ones.
 *
 * Call with no argument to restore the real `localStorage`.
 */
export function setSafeLocalStorageBackend(
  resolver?: () => SafeLocalStorageBackend | undefined
): void {
  resolveBackend = resolver ?? defaultBackend;
}

export const safeLocalStorage = {
  /**
   * Sets an item in localStorage.
   * @param {string} key - The key of the item.
   * @param {string} value - The value of the item.
   * @returns {boolean} - Returns true if the item was set successfully, otherwise false.
   */
  setItem(key: string, value: string): boolean {
    try {
      resolveBackend()?.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Error setting item in localStorage:', error);
      return false;
    }
  },

  /**
   * Retrieves an item from localStorage.
   * @param {string} key - The key of the item.
   * @returns {string|null} - Returns the value of the item, or null if an error occurred.
   */
  getItem(key: string): string | null {
    try {
      return resolveBackend()?.getItem(key) ?? null;
    } catch (error) {
      console.error('Error retrieving item from localStorage:', error);
      return null;
    }
  },

  /**
   * Removes an item from localStorage.
   * @param {string} key - The key of the item.
   * @returns {boolean} - Returns true if the item was removed successfully, otherwise false.
   */
  removeItem(key: string): boolean {
    try {
      resolveBackend()?.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing item from localStorage:', error);
      return false;
    }
  },
};
